/**
 * Simple JSONPath implementation for extracting data from JSON objects.
 * Supports basic path expressions like:
 * - $.key
 * - $.key.subkey
 * - $[0]
 * - $.key[0]
 * - $.key.subkey[1].field
 */
export function extractJsonPath(data: unknown, path: string | null | undefined): unknown {
  if (!path || path.trim() === "" || path === "$") {
    return data;
  }

  if (!path.startsWith("$")) {
    throw new Error('JSONPath must start with "$"');
  }

  let current: unknown = data;

  for (const part of parseJsonPathSegments(path)) {
    if (current === null || current === undefined) {
      return null;
    }

    if (typeof current !== "object") {
      throw new Error(`Cannot access property "${part}" on non-object value`);
    }

    if (Array.isArray(current)) {
      const index = parseInt(part, 10);
      if (isNaN(index)) {
        throw new Error(`Invalid array index: "${part}"`);
      }
      current = current[index];
    } else {
      current = (current as Record<string, unknown>)[part];
    }
  }

  return current;
}

/**
 * Splits a JSONPath expression into its key/index segments.
 */
export function parseJsonPathSegments(path: string): string[] {
  return path
    .slice(1)
    .split(/[\.\[\]]/)
    .filter(Boolean);
}

/**
 * Safely extracts JSONPath and returns the result as a formatted JSON string.
 * Returns the original data if path is invalid or extraction fails.
 */
export function extractJsonPathAsString(data: unknown, path: string | null | undefined): string {
  try {
    const extracted = extractJsonPath(data, path);
    return JSON.stringify(extracted, null, 2);
  } catch {
    return typeof data === "string" ? data : JSON.stringify(data, null, 2);
  }
}

/**
 * Builds a JSONPath query string by traversing the JSON tree path.
 * Given a parsed JSON object and a path of keys/indices, constructs
 * the corresponding JSONPath expression.
 *
 * @param path - Array of keys/indices from root to target (e.g., ["users", 0, "name"])
 * @returns JSONPath string (e.g., "$.users[0].name")
 */
export function buildJsonPath(path: (string | number)[]): string {
  if (path.length === 0) return "$";

  let result = "$";
  for (const segment of path) {
    if (typeof segment === "number") {
      result += `[${segment}]`;
    } else {
      result += `.${segment}`;
    }
  }
  return result;
}
